import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../utils/storage';
import { Player } from '../types';
import PlayerProfileModal from '../components/PlayerProfileModal';
import { toTurkishUpperCase } from '../utils/turkishUtils';
import { ensureHttps } from '../utils/urlUtils';
import './SquadPage.css';

interface SquadMarker {
  id: string;
  x: number; // yüzde olarak
  y: number; // yüzde olarak
  width: number; // yüzde olarak
  height: number; // yüzde olarak
  playerCode: string;
}

const SquadPage: React.FC = () => {
  const [squadImage, setSquadImage] = useState<string>('');
  const [markers, setMarkers] = useState<SquadMarker[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<SquadMarker | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showPlayerSelector, setShowPlayerSelector] = useState(false);
  const [markerToAssign, setMarkerToAssign] = useState<SquadMarker | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const isAdmin = storage.isAdminMode();

  useEffect(() => {
    loadSquadData();
  }, []);

  // Görsel yüklendikten sonra container boyutunu sabitle
  useEffect(() => {
    if (squadImage && imageContainerRef.current) {
      // Container'ın boyutunu sabitle - görsel yüklendikten sonra değişmesin
      const container = imageContainerRef.current;
      // Container'ın aspect ratio'sunu koru
      container.style.aspectRatio = '9 / 16';
    }
  }, [squadImage]);

  const loadSquadData = async () => {
    // Cache-first stratejisi ile hızlı yükleme
    const cacheFirst = async (getter: () => Promise<any>, cacheKey: string): Promise<any> => {
      const cached = localStorage.getItem(`futbol_cache_${cacheKey}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const timestamp = localStorage.getItem('futbol_cache_timestamp');
          if (timestamp && Date.now() - parseInt(timestamp) < 30 * 60 * 1000) {
            return parsed;
          }
        } catch (e) {}
      }
      return await getter();
    };

    // Oyuncuları cache'den hızlı yükle
    const allPlayers = await cacheFirst(() => storage.getPlayers(), 'players');
    setPlayers(allPlayers);
    
    // Görsel ve marker'ları ÖNCE yükle (await ile - geç yüklenmeyi önlemek için)
    // Retry mekanizması ile database lock sorunlarını önle
    const loadWithRetry = async (getter: () => Promise<any>, retries = 3, delay = 500): Promise<any> => {
      for (let i = 0; i < retries; i++) {
        try {
          return await getter();
        } catch (error: any) {
          if (error?.message?.includes('locked') && i < retries - 1) {
            console.warn(`[SquadPage] Database locked, retrying... (${i + 1}/${retries})`);
            await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
            continue;
          }
          throw error;
        }
      }
    };
    
    try {
      // Önce cache'den hızlı yükle (varsa) - Sadece URL formatındaki görselleri cache'le
      const cachedImage = localStorage.getItem('futbol_cache_squadImage');
      if (cachedImage) {
        try {
          // Eğer base64 formatındaysa cache'i temizle (quota aşımı önlemek için)
          if (cachedImage.startsWith('data:image')) {
            localStorage.removeItem('futbol_cache_squadImage');
            console.log('[SquadPage] Eski base64 cache temizlendi');
          } else {
            // URL formatındaki görseli göster
            const parsed = JSON.parse(cachedImage);
            if (parsed && parsed.trim() !== '' && !parsed.startsWith('data:image')) {
              setSquadImage(parsed);
              console.log('[SquadPage] Görsel cache\'den yüklendi (URL formatı)');
            }
          }
        } catch (e) {
          // Cache parse hatası, devam et
        }
      }
      
      // API'den güncel veriyi çek (retry ile)
      const [image, savedMarkers] = await Promise.all([
        loadWithRetry(() => storage.getSquadImage()),
        loadWithRetry(() => storage.getSquadMarkers()),
      ]);
      
      // Sadece API'den gelen veri varsa güncelle (cache'den gelen varsa öncelik ver)
      // Base64 formatındaki görselleri localStorage'a kaydetme
      if (image && image.trim() !== '' && !image.startsWith('data:image')) {
        setSquadImage(image);
      } else if (image && image.trim() !== '' && image.startsWith('data:image')) {
        // Base64 formatındaki görseli göster ama cache'leme
        setSquadImage(image);
        console.warn('[SquadPage] Base64 format görsel tespit edildi, cache\'lenmeyecek');
      }
      setMarkers(savedMarkers);
      console.log('[SquadPage] Görsel ve marker\'lar yüklendi:', { hasImage: !!image, markersCount: savedMarkers.length });
    } catch (error) {
      console.error('[SquadPage] Görsel veya marker yükleme hatası:', error);
      // Hata durumunda cache'den yükle (varsa) - Sadece URL formatındaki görselleri
      const cachedImage = localStorage.getItem('futbol_cache_squadImage');
      if (cachedImage) {
        try {
          // Base64 formatındaki görselleri cache'den yükleme (quota aşımı önlemek için)
          if (cachedImage.startsWith('data:image')) {
            console.warn('[SquadPage] Cache\'de base64 görsel var, yüklenmeyecek (quota aşımı önlemek için)');
          } else {
            const parsed = JSON.parse(cachedImage);
            if (parsed && parsed.trim() !== '' && !parsed.startsWith('data:image')) {
              setSquadImage(parsed);
              console.log('[SquadPage] Hata durumunda cache\'den görsel yüklendi (URL formatı)');
            }
          }
        } catch (e) {
          // Cache parse hatası
        }
      }
      // Marker'lar için boş array set et
      if (markers.length === 0) {
        setMarkers([]);
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    try {
      console.log('[SquadPage] Kadro görseli yükleniyor...', file.name);
      
      // Görseli hosting'e yükle (base64 yerine URL kullan)
      const imageUrl = await storage.uploadImage(file, `squad/kadro-${Date.now()}-${file.name}`);
      
      console.log('[SquadPage] Kadro görseli yüklendi:', imageUrl);
      
      // URL'i kaydet
      await storage.saveSquadImage(imageUrl);
      
      // API'nin güncellenmesi için kısa bir bekleme
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Doğrulama - API'den görseli çek
      const verifyImage = await storage.getSquadImage();
      
      if (verifyImage === imageUrl) {
        console.log('[SquadPage] ✅ Kadro görseli başarıyla kaydedildi ve doğrulandı');
        setSquadImage(imageUrl);
        alert('Kadro görseli başarıyla yüklendi!');
      } else {
        console.warn('[SquadPage] ⚠️ Kadro görseli farklı URL ile kaydedilmiş:', {
          expected: imageUrl,
          actual: verifyImage
        });
        setSquadImage(imageUrl);
        alert('Kadro görseli yüklendi ancak doğrulanamadı. Sayfa yenilenecek.');
        window.location.reload();
      }
    } catch (error: any) {
      console.error('[SquadPage] Kadro görseli yükleme hatası:', error);
      const errorMessage = error?.message || 'Bilinmeyen hata';
      alert('Kadro görseli yüklenirken bir hata oluştu: ' + errorMessage + '\n\nLütfen Console\'u (F12) kontrol edin.');
    }
    
    e.target.value = '';
  };

  const handleAddMarker = () => {
    const newMarker: SquadMarker = {
      id: `marker-${Date.now()}`,
      x: 50, // görselin ortası
      y: 50,
      width: 5, // yüzde olarak
      height: 5,
      playerCode: '',
    };
    setMarkers([...markers, newMarker]);
    setSelectedMarker(newMarker);
    setMarkerToAssign(newMarker);
    setShowPlayerSelector(true);
  };

  const handleMarkerClick = (marker: SquadMarker, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Mobilde scroll'u önle
    // Mobilde drag sonrası click'i önle
    if (isDragging || isResizing) {
      return;
    }
    
    if (isAdmin) {
      setSelectedMarker(marker);
      setMarkerToAssign(marker);
      setShowPlayerSelector(true);
    } else {
      // Yönetici değilse oyuncu profilini aç
      const player = players.find(p => p.code === marker.playerCode);
      if (player) {
        setSelectedPlayer(player);
      }
    }
  };

  const getContainerRect = () => {
    return imageContainerRef.current?.getBoundingClientRect();
  };

  const handleMarkerMouseDown = (marker: SquadMarker, e: React.MouseEvent | React.TouchEvent) => {
    if (!isAdmin) return;
    e.stopPropagation();
    e.preventDefault(); // Mobilde scroll'u önle
    setSelectedMarker(marker);
    setIsDragging(true);
    const rect = getContainerRect();
    if (rect) {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const markerX = rect.left + (rect.width * marker.x / 100);
      const markerY = rect.top + (rect.height * marker.y / 100);
      setDragStart({
        x: clientX - markerX,
        y: clientY - markerY,
      });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isAdmin || !selectedMarker) return;
    e.stopPropagation();
    setIsResizing(true);
    const rect = getContainerRect();
    if (rect) {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      setDragStart({
        x: clientX - (rect.left + (rect.width * (selectedMarker.x + selectedMarker.width) / 100)),
        y: clientY - (rect.top + (rect.height * (selectedMarker.y + selectedMarker.height) / 100)),
      });
    }
  };

  const handleMove = (clientX: number, clientY: number) => {
    if (!imageContainerRef.current || (!isDragging && !isResizing) || !selectedMarker) return;
    
    const rect = imageContainerRef.current.getBoundingClientRect();
    const xPercent = ((clientX - rect.left - dragStart.x) / rect.width) * 100;
    const yPercent = ((clientY - rect.top - dragStart.y) / rect.height) * 100;

    if (isDragging) {
      const newX = Math.max(0, Math.min(100, xPercent));
      const newY = Math.max(0, Math.min(100, yPercent));
      
      const updatedMarkers = markers.map(m => 
        m.id === selectedMarker.id 
          ? { ...m, x: newX, y: newY }
          : m
      );
      setMarkers(updatedMarkers);
      setSelectedMarker({ ...selectedMarker, x: newX, y: newY });
    } else if (isResizing) {
      const newWidth = Math.max(2, Math.min(100 - selectedMarker.x, xPercent - selectedMarker.x));
      const newHeight = Math.max(2, Math.min(100 - selectedMarker.y, yPercent - selectedMarker.y));
      
      const updatedMarkers = markers.map(m => 
        m.id === selectedMarker.id 
          ? { ...m, width: newWidth, height: newHeight }
          : m
      );
      setMarkers(updatedMarkers);
      setSelectedMarker({ ...selectedMarker, width: newWidth, height: newHeight });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length > 0 && (isDragging || isResizing)) {
      e.preventDefault();
      e.stopPropagation();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleDeleteMarker = (markerId: string, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Mobilde scroll'u önle
    setMarkers(markers.filter(m => m.id !== markerId));
    if (selectedMarker?.id === markerId) {
      setSelectedMarker(null);
    }
    console.log('[SquadPage] Marker silindi:', markerId);
  };

  const handleSave = async () => {
    await storage.saveSquadMarkers(markers);
    alert('Kadro ayarları kaydedildi!');
  };

  const handlePlayerSelect = (playerCode: string) => {
    if (markerToAssign) {
      const updatedMarkers = markers.map(m => 
        m.id === markerToAssign.id 
          ? { ...m, playerCode }
          : m
      );
      setMarkers(updatedMarkers);
      setSelectedMarker(updatedMarkers.find(m => m.id === markerToAssign.id) || null);
    }
    setShowPlayerSelector(false);
    setMarkerToAssign(null);
  };

  const getPlayerByCode = (code: string): Player | null => {
    return players.find(p => p.code === code) || null;
  };

  return (
    <div className="squad-page">
      <h1 className="page-title">KADRO</h1>

      {isAdmin && (
        <div className="admin-controls-squad">
          <div className="admin-image-upload">
            <label>
              Kadro Görseli Yükle (9:16)
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          <div className="admin-marker-controls">
            <button className="add-marker-btn" onClick={handleAddMarker}>
              + Obje Ekle
            </button>
            <button className="save-squad-btn" onClick={handleSave}>
              Kaydet
            </button>
          </div>
        </div>
      )}

      <div 
        className="squad-image-container"
        ref={imageContainerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => {
          // Container'daki touch end - sadece drag/resize bitir
          if (isDragging || isResizing) {
            handleTouchEnd();
          }
        }}
        onTouchCancel={handleTouchEnd}
      >
        {squadImage ? (
          <>
            <img 
              src={ensureHttps(squadImage)} 
              alt="Kadro" 
              className="squad-image"
              onLoad={() => {
                // Görsel yüklendikten sonra container boyutunu sabitle
                if (imageContainerRef.current) {
                  const container = imageContainerRef.current;
                  // Container'ın aspect ratio'sunu koru
                  container.style.aspectRatio = '9 / 16';
                }
              }}
            />
            {markers.map(marker => {
              const player = getPlayerByCode(marker.playerCode);
              return (
                <div
                  key={marker.id}
                  className={`squad-marker ${isAdmin ? 'admin' : ''} ${selectedMarker?.id === marker.id ? 'selected' : ''} ${(isDragging && selectedMarker?.id === marker.id) ? 'dragging' : ''} ${(isResizing && selectedMarker?.id === marker.id) ? 'resizing' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${marker.x}%`,
                    top: `${marker.y}%`,
                    width: `${Math.max(marker.width, 2)}%`,
                    height: `${Math.max(marker.height, 2)}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  onClick={(e) => {
                    // Sadece mouse click için (touch için onTouchEnd kullanılacak)
                    // Mobilde onClick çalışmaz, onTouchEnd kullanılır
                    if (!isDragging && !isResizing && !('touches' in e)) {
                      handleMarkerClick(marker, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (isAdmin) {
                      handleMarkerMouseDown(marker, e);
                    }
                  }}
                  onTouchStart={(e) => {
                    // Passive listener hatası önlemek için sadece gerekli durumlarda preventDefault
                    if (isAdmin || e.currentTarget === e.target) {
                      e.preventDefault();
                    }
                    e.stopPropagation();
                    if (isAdmin) {
                      // Yönetici modunda drag başlat
                      handleMarkerMouseDown(marker, e);
                    } else {
                      // Yönetici değilse sadece seç (drag yok)
                      setSelectedMarker(marker);
                      // Touch başlangıç zamanını kaydet (kısa touch = click, uzun touch = scroll)
                      const touchStartTime = Date.now();
                      (e.currentTarget as HTMLElement).setAttribute('data-touch-start', touchStartTime.toString());
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Touch işlemi bittiğinde drag'i bitir
                    const wasDragging = isDragging;
                    const wasResizing = isResizing;
                    setIsDragging(false);
                    setIsResizing(false);
                    
                    if (isAdmin) {
                      // Yönetici modunda: Eğer drag/resize yapılmadıysa click işlemi yap
                      if (!wasDragging && !wasResizing) {
                        // Kısa bir gecikme ile click işlemini yap (double-tap önlemek için)
                        setTimeout(() => {
                          handleMarkerClick(marker, e);
                        }, 100);
                      }
                    } else {
                      // Yönetici değilse: Touch süresini kontrol et (kısa touch = click)
                      const touchStartTime = (e.currentTarget as HTMLElement).getAttribute('data-touch-start');
                      if (touchStartTime) {
                        const touchDuration = Date.now() - parseInt(touchStartTime);
                        // 300ms'den kısa touch = click (uzun touch = scroll)
                        if (touchDuration < 300) {
                          handleMarkerClick(marker, e);
                        }
                      } else {
                        // Touch start zamanı yoksa direkt click yap
                        handleMarkerClick(marker, e);
                      }
                    }
                  }}
                >
                  {isAdmin && (
                    <>
                      <div className="marker-label">
                        {player ? player.code : 'Oyuncu Seç'}
                      </div>
                      <button 
                        className="marker-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteMarker(marker.id, e);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteMarker(marker.id, e);
                        }}
                      >
                        ×
                      </button>
                      {selectedMarker?.id === marker.id && (
                        <div 
                          className="marker-resize-handle"
                          onMouseDown={handleResizeMouseDown}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            handleResizeMouseDown(e);
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="squad-placeholder">
            {isAdmin ? (
              <p>Kadro görseli yüklemek için yukarıdaki butonu kullanın.</p>
            ) : (
              <p>Kadro görseli henüz yüklenmemiş.</p>
            )}
          </div>
        )}
      </div>

      {showPlayerSelector && (
        <div className="player-selector-modal" onClick={() => setShowPlayerSelector(false)}>
          <div className="player-selector-content" onClick={(e) => e.stopPropagation()}>
            <h3>Oyuncu Seç</h3>
            <div className="player-list">
              {players.map(player => (
                <div
                  key={player.id}
                  className="player-selector-item"
                  onClick={() => handlePlayerSelect(player.code)}
                >
                  {player.code} - {toTurkishUpperCase(player.name)}
                </div>
              ))}
            </div>
            <button 
              className="cancel-selector-btn"
              onClick={() => {
                setShowPlayerSelector(false);
                setMarkerToAssign(null);
              }}
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          isCompact={false}
        />
      )}
    </div>
  );
};

export default SquadPage;
